import { Link } from 'react-router-dom';
import { useEventManager } from '../hooks/useEventManager';

const eventMoments = [
  {
    number: '01',
    title: 'Celebrate together',
    description:
      "Each Victoria's Place event is designed to feel welcoming, joyful, and centered on family and community.",
  },
  {
    number: '02',
    title: 'Join in the way that fits',
    description:
      'Guests, vendors, and community partners can each find the right way to participate and support the mission.',
  },
  {
    number: '03',
    title: 'Stay connected to the mission',
    description:
      'Events help create lasting visibility, encouragement, and support for children with Down Syndrome and their families.',
  },
];

export default function Events() {
  const { featuredEvent, publishedEvents, isLoading } = useEventManager();

  if (isLoading) {
    return (
      <div className="page-shell events-page space-y-8">
        <section className="events-hero">
          <div className="events-hero-copy">
            <span className="eyebrow">Events</span>
            <p className="events-kicker">Loading the Victoria&apos;s Place events hub</p>
            <h1>The published event list is loading.</h1>
          </div>
        </section>
      </div>
    );
  }

  if (!featuredEvent) {
    return (
      <div className="page-shell events-page space-y-8">
        <section className="events-hero">
          <div className="events-hero-copy">
            <span className="eyebrow">Events</span>
            <p className="events-kicker">Published events should be clear, visible, and easy to act on</p>
            <h1>The events hub is ready for the first published event.</h1>
            <p className="events-hero-lead">
              Upcoming gatherings, fundraisers, and community moments will appear here as soon as they are ready to
              share.
            </p>
            <div className="events-hero-actions">
              <Link to="/contact" className="button-primary tap-target">
                Ask about upcoming events
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-shell events-page space-y-8">
      <section className="events-hero">
        <div className="events-hero-copy">
          <span className="eyebrow">Events</span>
          <p className="events-kicker">Published events should be clear, visible, and easy to act on</p>
          <h1>Gather, celebrate, and support the mission together.</h1>
          <p className="events-hero-lead">
            From community gatherings to fundraising moments, Victoria&apos;s Place events are meant to bring families,
            supporters, vendors, and neighbors into one welcoming experience.
          </p>
          <div className="events-hero-actions">
            <Link to={`/events/${featuredEvent.slug}`} className="button-primary tap-target">
              View featured event
            </Link>
            <Link to="/contact" className="button-secondary tap-target">
              Ask about participation
            </Link>
          </div>
        </div>

        <div className="events-hero-media">
          <figure className="events-photo-card events-photo-card--main">
            <img src={featuredEvent.heroImage} alt={featuredEvent.title} />
          </figure>
          {featuredEvent.galleryImages.length > 1 ? (
            <div className="events-photo-strip">
              {featuredEvent.galleryImages.slice(1, 3).map((image, index) => (
                <figure key={`${image}-${index}`} className="events-photo-card events-photo-card--support">
                  <img src={image} alt={`${featuredEvent.title} gallery image ${index + 1}`} />
                </figure>
              ))}
            </div>
          ) : null}
          <div className="events-note-card">
            <p className="info-kicker">Featured now</p>
            <p>{featuredEvent.title}</p>
            <span className="events-note-meta">
              {featuredEvent.dateLabel} · {featuredEvent.locationName}
            </span>
          </div>
        </div>
      </section>

      <section className="events-featured-panel">
        <div className="events-featured-copy">
          <p className="home-section-label">Featured event</p>
          <h2>{featuredEvent.title}</h2>
          <p>{featuredEvent.fullDescription}</p>

          <div className="events-ticket-row">
            {featuredEvent.tickets.map((ticket) => (
              <article key={ticket.id} className="events-ticket-card">
                <p className="info-kicker">{ticket.category.replace('-', ' ')}</p>
                <h3>{ticket.name}</h3>
                <strong>{ticket.priceLabel}</strong>
                <p>{ticket.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="events-list-section">
        <div className="events-section-heading">
          <p className="home-section-label">Published events</p>
          <h2>See what&apos;s happening next with Victoria&apos;s Place.</h2>
        </div>

        <div className="events-option-grid">
          {publishedEvents.map((event) => (
            <article key={event.id} className="events-option-card">
              <p className="info-kicker">{event.status.replace('-', ' ')}</p>
              <h3>{event.title}</h3>
              <p>{event.shortDescription}</p>
              <div className="events-card-meta">
                <span>{event.dateLabel}</span>
                <span>{event.locationName}</span>
              </div>
              <Link to={`/events/${event.slug}`} className="button-secondary tap-target w-fit">
                View event page
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="events-process">
        <div className="events-process-copy">
          <p className="home-section-label">What to Expect</p>
          <h2>Every event is built to welcome families, partners, and supporters.</h2>
          <p>
            Whether you attend as a guest, support as a vendor, or simply want to stay connected, each event is meant
            to reflect the same spirit of encouragement, belonging, and joy that defines Victoria&apos;s Place.
          </p>
        </div>

        <div className="events-step-list">
          {eventMoments.map((step) => (
            <article key={step.number} className="events-step-card">
              <span>{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
